import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, FileText, Wallet, type LucideIcon } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import { Seccion } from '@/shared/ui/Seccion';
import { ListaDeDeudores } from './ListaDeDeudores';
import { Barras, enDinero, enPorcentaje } from '@/shared/ui/graficos';

interface Tramo {
  antiguedad: string;
  clientes: number;
  facturas: number;
  monto: number;
}

interface Deudor {
  cliente: string;
  identificacion: string;
  facturas: number;
  masVieja: string;
  mora: number;
  total: number;
}

interface Panel {
  total: number;
  vencido: number;
  facturas: number;
  tramos: Tramo[];
  deudores: Deudor[];
}

export function PanelDeCartera() {
  const panel = useQuery({
    queryKey: ['informe-cartera'],
    queryFn: () => api.get<Panel>('/informes/facturacion/cartera/panel'),
  });

  if (panel.isLoading) {
    return <Cargando titulo="Cargando" detalle="calculando la cartera de hoy" />;
  }

  const datos = panel.data;
  if (!datos || datos.tramos.length === 0) {
    return (
      <p className="rounded-2xl border border-armazon-200 bg-white p-12 text-center text-sm uppercase text-armazon-400">
        No hay deuda pendiente
      </p>
    );
  }

  const porcentajeVencido = datos.total > 0 ? (datos.vencido / datos.total) * 100 : 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Cifra
          icono={Wallet}
          rotulo="Deuda total"
          valor={enDinero(datos.total)}
          tono="text-marca-700"
          fondo="bg-marca-50"
        />
        <Cifra
          icono={AlertTriangle}
          rotulo="Vencido"
          valor={enDinero(datos.vencido)}
          tono="text-red-700"
          fondo="bg-red-50"
          ayuda="Lo que ya pasó de su fecha de pago"
        />
        <Cifra
          icono={AlertTriangle}
          rotulo="% vencido"
          valor={enPorcentaje(porcentajeVencido)}
          tono="text-amber-700"
          fondo="bg-amber-50"
          ayuda="Un total alto con todo por vencer es normal; con todo vencido, no"
        />
        <Cifra
          icono={FileText}
          rotulo="Facturas"
          valor={datos.facturas.toLocaleString('es-EC')}
          tono="text-blue-700"
          fondo="bg-blue-50"
        />
      </div>

      <Seccion
        titulo="Deuda por antigüedad"
        ayuda="Cuánto lleva sin pagarse cada parte de la cartera"
      >
        <Barras datos={datos.tramos} claveEtiqueta="antiguedad" claveValor="monto" nombre="Deuda" />
      </Seccion>

      <Seccion titulo="Detalle por tramo" ayuda="Los mismos números del gráfico, con su recuento">
        <Tabla
          columnas={['Antigüedad', 'Clientes', 'Facturas', 'Monto']}
          filas={datos.tramos.map((t) => [
            t.antiguedad,
            t.clientes.toLocaleString('es-EC'),
            t.facturas.toLocaleString('es-EC'),
            enDinero(t.monto),
          ])}
        />
      </Seccion>

      <Seccion
        titulo="Pendientes de cobro"
        ayuda="Todos los que deben hoy, de mayor a menor. Lo que está en un convenio no cuenta"
      >
        <ListaDeDeudores />
      </Seccion>
    </div>
  );
}

function Cifra({
  icono: Icono,
  rotulo,
  valor,
  tono,
  fondo,
  ayuda,
}: {
  icono: LucideIcon;
  rotulo: string;
  valor: string;
  tono: string;
  fondo: string;
  ayuda?: string;
}) {
  return (
    <div className={`rounded-2xl border border-white/60 p-4 ${fondo}`} title={ayuda}>
      <span className="mb-2 flex items-center gap-2">
        <Icono className={`size-4 ${tono}`} aria-hidden />
        <span className="text-[10px] font-bold uppercase tracking-wider text-armazon-500">
          {rotulo}
        </span>
      </span>
      <span className={`block truncate text-xl font-bold tabular-nums ${tono}`}>{valor}</span>
    </div>
  );
}

function Tabla({
  columnas,
  filas,
  alineadas,
}: {
  columnas: string[];
  filas: string[][];
  alineadas?: boolean[];
}) {
  const aLaDerecha = (i: number) => alineadas?.[i] ?? i > 0;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-armazon-50/80">
          <tr>
            {columnas.map((c, i) => (
              <th
                key={c}
                scope="col"
                className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-armazon-500 ${
                  aLaDerecha(i) ? 'text-right' : 'text-left'
                }`}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-armazon-100">
          {filas.map((fila) => (
            <tr key={fila.join('|')} className="transition-colors hover:bg-armazon-50/60">
              {fila.map((celda, i) => (
                <td
                  key={i}
                  className={`px-3 py-2.5 text-xs tabular-nums ${
                    aLaDerecha(i) ? 'text-right text-armazon-700' : 'text-left text-armazon-700'
                  } ${i === columnas.length - 1 ? 'font-semibold' : ''}`}
                >
                  {celda}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
